import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthHeader } from "../../utils/authHeader";
import CustomToast from "../../components/misc/CustomToast";
import { Button } from "../../components/misc/Button";
import { Label } from "../../components/misc/Label";
import { Switch } from "../../components/misc/Switch";
import { Textarea } from "../../components/misc/Textarea";
import { cn } from "../../utils/classMerger";

// Matches @MaxLength on the api's update DTO.
const REASON_MAX_LENGTH = 300;

// MB-1611: the MLS (Trestle) feed switch. Two lanes of OBSERVED health, written
// by the code that talks to Trestle and by the 5-minute probe, plus the admin
// override: "forced offline" holds the feed down whatever is observed (a vendor
// maintenance window, or a detector we disagree with) until "Back to automatic".

interface LaneState {
    ok?: boolean;
    since?: string;
    consecutiveFailures?: number;
    lastSuccessAt?: string;
    lastFailureAt?: string;
    lastFailureSource?: string;
    lastFailureDetail?: string;
    offlineAlertSentAt?: string;
}

export interface MlsFeedAdminStatus {
    mode: "auto" | "forcedOffline";
    reason?: string;
    forcedBy?: string;
    forcedAt?: string;
    data?: LaneState;
    photos?: LaneState;
    lastProbeAt?: string;
    effective: { online: boolean; data: boolean; photos: boolean; message?: string };
    waitingListings: number;
}

const fmt = (iso?: string): string => (iso ? new Date(iso).toLocaleString() : "—");

const LaneCard: React.FC<{ title: string; what: string; lane?: LaneState; effectiveUp: boolean }> = ({
    title,
    what,
    lane,
    effectiveUp,
}) => {
    const observedUp = lane?.ok !== false;
    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-1 text-sm">
            <div className="flex items-center justify-between">
                <span className="font-semibold text-primary">{title}</span>
                <span
                    className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-semibold",
                        effectiveUp
                            ? "bg-[#43a047]/15 text-[#43a047]"
                            : "bg-[#d50000]/10 text-[#c62828]"
                    )}
                >
                    {effectiveUp ? "Up" : observedUp ? "Held offline" : "Down"}
                </span>
            </div>
            <p className="text-gray-500">{what}</p>
            <p>
                <span className="text-gray-500">Observed:</span> {observedUp ? "healthy" : "failing"}{" "}
                since {fmt(lane?.since)}
            </p>
            <p>
                <span className="text-gray-500">Last success:</span> {fmt(lane?.lastSuccessAt)}
            </p>
            <p>
                <span className="text-gray-500">Last failure:</span> {fmt(lane?.lastFailureAt)}
                {lane?.lastFailureSource ? ` (${lane.lastFailureSource})` : ""}
            </p>
            {lane?.lastFailureDetail && (
                <p className="break-words font-mono text-xs text-gray-600">{lane.lastFailureDetail}</p>
            )}
        </div>
    );
};

const MlsFeedStatusPanel: React.FC = () => {
    const [status, setStatus] = useState<MlsFeedAdminStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [forceOffline, setForceOffline] = useState(false);
    const [reason, setReason] = useState("");

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const response = await axios.get<MlsFeedAdminStatus>(`${backendUrl}/admin/mls-feed-status`, {
                headers,
            });
            setStatus(response.data);
            setForceOffline(response.data.mode === "forcedOffline");
            setReason(response.data.reason ?? "");
        } catch (error) {
            console.error(error);
            toast.error(<CustomToast message="Failed to load the MLS feed status" type="error" />, {
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    }, [backendUrl]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        if (!status) return;
        if (forceOffline && reason.trim() === "") {
            toast.error(
                <CustomToast message="Give a reason — customers see it on their Listings page." type="error" />,
                { autoClose: 3000 },
            );
            return;
        }
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const response = await axios.post<MlsFeedAdminStatus>(
                `${backendUrl}/admin/mls-feed-status`,
                forceOffline ? { mode: "forcedOffline", reason: reason.trim() } : { mode: "auto" },
                { headers },
            );
            setStatus(response.data);
            setForceOffline(response.data.mode === "forcedOffline");
            setReason(response.data.reason ?? "");
            toast.success(
                <CustomToast
                    message={
                        forceOffline ? "MLS feed held offline." : "MLS feed back to automatic detection."
                    }
                    type="success"
                />,
                { autoClose: 3000 },
            );
        } catch (error) {
            console.error(error);
            toast.error(<CustomToast message="Failed to update the MLS feed status" type="error" />, {
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const changed =
        status !== null &&
        (forceOffline !== (status.mode === "forcedOffline") ||
            (forceOffline && reason.trim() !== (status.reason ?? "").trim()));

    return (
        <div className="space-y-4">
            <span className="text-sm text-gray-500">
                Health of the MLS (Trestle) feed as observed by the app and its 5-minute probe.
                While a lane is down, new listings wait instead of failing, review-list adds are
                queued, and the hourly sync makes no expiry decisions. Force the feed offline to
                hold that behaviour deliberately (for example a vendor maintenance window).
            </span>

            {status && (
                <div className="grid gap-3 md:grid-cols-2">
                    <LaneCard
                        title="Listing data"
                        what="Sign-in token, listing records, photo lists, open houses."
                        lane={status.data}
                        effectiveUp={status.effective.data}
                    />
                    <LaneCard
                        title="Photo downloads"
                        what="The image files behind each listing photo."
                        lane={status.photos}
                        effectiveUp={status.effective.photos}
                    />
                </div>
            )}

            {status && (
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
                    <span>
                        <span className="text-gray-500">Mode:</span>{" "}
                        {status.mode === "forcedOffline" ? (
                            <>
                                forced offline by {status.forcedBy ?? "an admin"} at {fmt(status.forcedAt)}
                            </>
                        ) : (
                            "automatic"
                        )}
                    </span>
                    <span>
                        <span className="text-gray-500">Listings waiting on the feed:</span>{" "}
                        <span className="font-semibold">{status.waitingListings}</span>
                    </span>
                    <span>
                        <span className="text-gray-500">Last probe:</span> {fmt(status.lastProbeAt)}
                    </span>
                </div>
            )}

            <div className="flex items-center justify-between">
                <Label htmlFor="mls-feed-force" className="text-base">
                    Force the MLS feed offline
                </Label>
                <Switch
                    id="mls-feed-force"
                    checked={forceOffline}
                    onCheckedChange={() => setForceOffline((v) => !v)}
                />
            </div>
            <div className="space-y-2">
                <Label htmlFor="mls-feed-reason">Reason shown to customers</Label>
                <Textarea
                    id="mls-feed-reason"
                    placeholder="e.g. Our MLS provider has scheduled maintenance tonight; new listings will finish automatically afterwards."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={!forceOffline}
                    maxLength={REASON_MAX_LENGTH}
                    className="min-h-[80px]"
                />
                <p className="text-right text-xs text-gray-500">
                    {reason.length} / {REASON_MAX_LENGTH}
                </p>
            </div>
            <div className="flex gap-2">
                <Button onClick={save} disabled={loading || !changed}>
                    {loading ? "Saving..." : forceOffline ? "Hold feed offline" : "Back to automatic"}
                </Button>
                <Button variant="outline" onClick={load} disabled={loading}>
                    Refresh
                </Button>
            </div>
        </div>
    );
};

export default MlsFeedStatusPanel;
